/**
 * Localization and Formatting Utilities
 *
 * Provides locale-aware formatting for dates, numbers, currencies, and relative time.
 * Supports both English and Portuguese locales.
 */

import { useLocale } from 'next-intl';

/**
 * Get the current locale for formatting
 */
export function useFormattingLocale(): string {
  const locale = useLocale();
  return locale === 'pt' ? 'pt-PT' : 'en-US';
}

/**
 * Format numbers with locale-specific formatting
 */
export function formatNumber(value: number | null | undefined, locale: string = 'en-US'): string {
  if (value == null || isNaN(value)) {
    return '0';
  }
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Format currency with locale-specific formatting
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string = 'USD',
  locale: string = 'en-US',
): string {
  if (value == null || isNaN(value)) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(0);
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(value);
}

/**
 * Format percentage with locale-specific formatting
 */
export function formatPercentage(
  value: number | null | undefined,
  locale: string = 'en-US',
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = 1,
): string {
  if (value == null || isNaN(value)) {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(0);
  }
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value / 100);
}

/**
 * Format date with locale-specific formatting
 */
export function formatDate(
  date: Date | string | null | undefined,
  locale: string = 'en-US',
  options: Intl.DateTimeFormatOptions = {},
): string {
  if (!date) {
    return 'Unknown date';
  }

  let dateObj: Date;

  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return 'Invalid date';
  }

  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(dateObj);
}

/**
 * Format time with locale-specific formatting
 */
export function formatTime(
  date: Date | string | null | undefined,
  locale: string = 'en-US',
  options: Intl.DateTimeFormatOptions = {},
): string {
  if (!date) {
    return 'Unknown time';
  }

  let dateObj: Date;

  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return 'Invalid time';
  }

  if (isNaN(dateObj.getTime())) {
    return 'Invalid time';
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(dateObj);
}

/**
 * Format date and time with locale-specific formatting
 */
export function formatDateTime(
  date: Date | string | null | undefined,
  locale: string = 'en-US',
  options: Intl.DateTimeFormatOptions = {},
): string {
  if (!date) {
    return 'Unknown date/time';
  }

  let dateObj: Date;

  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return 'Invalid date/time';
  }

  if (isNaN(dateObj.getTime())) {
    return 'Invalid date/time';
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  return new Intl.DateTimeFormat(locale, { ...defaultOptions, ...options }).format(dateObj);
}

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 */
export function formatRelativeTime(
  date: Date | string | null | undefined,
  locale: string = 'en-US',
  baseDate: Date = new Date(),
): string {
  // Handle null, undefined, or invalid date inputs
  if (!date) {
    return 'Unknown time';
  }

  let dateObj: Date;

  // Convert to Date object if it's a string
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return 'Invalid date';
  }

  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const diffInSeconds = Math.floor((dateObj.getTime() - baseDate.getTime()) / 1000);

  // Define time units in seconds
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 365 * 24 * 60 * 60],
    ['month', 30 * 24 * 60 * 60],
    ['week', 7 * 24 * 60 * 60],
    ['day', 24 * 60 * 60],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
  ];

  for (const [unit, secondsInUnit] of units) {
    const value = Math.floor(Math.abs(diffInSeconds) / secondsInUnit);
    if (value >= 1) {
      return rtf.format(diffInSeconds < 0 ? -value : value, unit);
    }
  }

  return rtf.format(0, 'second');
}

/**
 * Format compact numbers (e.g., 1.2K, 3.4M)
 */
export function formatCompactNumber(
  value: number | null | undefined,
  locale: string = 'en-US',
): string {
  if (value == null || isNaN(value)) {
    return '0';
  }
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format weight with appropriate unit (kg or tonnes)
 * Automatically converts to tonnes if >= 1000 kg
 */
export function formatWeight(
  valueInKg: number | null | undefined,
  locale: string = 'en-US',
): string {
  if (valueInKg == null || isNaN(valueInKg)) {
    return '0 kg';
  }

  // Convert to tonnes if >= 1000 kg
  if (valueInKg >= 1000) {
    const tonnes = valueInKg / 1000;
    return `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: 1,
    }).format(tonnes)} t`;
  }

  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(valueInKg)} kg`;
}

/**
 * Format ordinal numbers (e.g., 1st, 2nd, 3rd)
 */
export function formatOrdinal(value: number | null | undefined, locale: string = 'en-US'): string {
  if (value == null || isNaN(value)) {
    return '0th';
  }

  const pr = new Intl.PluralRules(locale, { type: 'ordinal' });
  const suffixes = new Map([
    ['one', 'st'],
    ['two', 'nd'],
    ['few', 'rd'],
    ['other', 'th'],
  ]);

  const rule = pr.select(value);
  const suffix = suffixes.get(rule) || 'th';

  return `${value}${suffix}`;
}

/**
 * Format duration in a human-readable way
 */
export function formatDuration(
  milliseconds: number | null | undefined,
  locale: string = 'en-US',
): string {
  if (milliseconds == null || isNaN(milliseconds) || milliseconds < 0) {
    return 'Unknown duration';
  }

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return new Intl.RelativeTimeFormat(locale).format(days, 'day');
  } else if (hours > 0) {
    return new Intl.RelativeTimeFormat(locale).format(hours, 'hour');
  } else if (minutes > 0) {
    return new Intl.RelativeTimeFormat(locale).format(minutes, 'minute');
  } else {
    return new Intl.RelativeTimeFormat(locale).format(seconds, 'second');
  }
}

/**
 * Get locale-specific currency code
 */
export function getLocaleCurrency(locale: string): string {
  const currencyMap: Record<string, string> = {
    'en-US': 'USD',
    'pt-PT': 'EUR',
    'en-GB': 'GBP',
    'de-DE': 'EUR',
    'fr-FR': 'EUR',
    'es-ES': 'EUR',
  };

  return currencyMap[locale] || 'USD';
}

/**
 * Hook for getting formatting functions with current locale
 */
export function useFormatting() {
  const locale = useFormattingLocale();
  const currency = getLocaleCurrency(locale);

  return {
    locale,
    currency,
    formatNumber: (value: number | null | undefined) => formatNumber(value, locale),
    formatCurrency: (value: number | null | undefined, currencyCode?: string) =>
      formatCurrency(value, currencyCode || currency, locale),
    formatPercentage: (value: number | null | undefined, minDigits?: number, maxDigits?: number) =>
      formatPercentage(value, locale, minDigits, maxDigits),
    formatDate: (date: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions) =>
      formatDate(date, locale, options),
    formatTime: (date: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions) =>
      formatTime(date, locale, options),
    formatDateTime: (
      date: Date | string | null | undefined,
      options?: Intl.DateTimeFormatOptions,
    ) => formatDateTime(date, locale, options),
    formatRelativeTime: (date: Date | string | null | undefined, baseDate?: Date) =>
      formatRelativeTime(date, locale, baseDate),
    formatCompactNumber: (value: number | null | undefined) => formatCompactNumber(value, locale),
    formatOrdinal: (value: number | null | undefined) => formatOrdinal(value, locale),
    formatDuration: (milliseconds: number | null | undefined) =>
      formatDuration(milliseconds, locale),
    formatWeight: (valueInKg: number | null | undefined) => formatWeight(valueInKg, locale),
  };
}
